import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { contractPdfQueryOptions, useUser } from '~/api/user';
import { AlertDialog, Button, Dialog, Illustration, Tabs } from '~/components';
import { accountCompletionSteps, getAccountCompletionProgress } from '~/utils/user';

import type { CompleteAccountDialogProps as Props } from './types';

import { successIllustrationStyle, tabContentStyle, tabListStyle } from './CompleteAccountDialog.css';
import { FinancialInfoTab } from './TabPanels/FinancialInfoTab';
import { FrameworkAgreementTab } from './TabPanels/FrameworkAgreementTab';
import { PeppolTab } from './TabPanels/PeppolTab';
import { VatRegulationTab } from './TabPanels/VatRegulationTab';

type TabId = (typeof accountCompletionSteps)[number];

const isTabId = (value: unknown): value is TabId => (accountCompletionSteps as readonly unknown[]).includes(value);

export const CompleteAccountDialog = ({ onClose, onComplete }: Props) => {
  const { data: user } = useUser();
  const queryClient = useQueryClient();

  const [isCompleted, setIsCompleted] = useState(false);
  const { t } = useTranslation(['common', 'settings']);

  useEffect(() => {
    // /user/contract only returns the document still pending signature - once the user has already
    // signed everything there's nothing left to prefetch, and the request would fail.
    if (!(user.contractSigned && user.addendumSigned)) {
      queryClient.prefetchQuery(contractPdfQueryOptions(false));
    }
  }, [queryClient, user.addendumSigned, user.contractSigned]);

  const tabs = useMemo(() => {
    const progress = getAccountCompletionProgress(user);

    return accountCompletionSteps.map((step) => ({
      id: step,
      completed: progress[step],
      label: t(`settings:accountCompletionModal.${step}.tab`),
    }));
  }, [t, user]);

  const [selectedTabId, setSelectedTabId] = useState<TabId>(() => tabs.find((tab) => !tab.completed)?.id ?? tabs[0].id);

  const onTabCompleted = (tabId: TabId) => () => {
    const nextIncompleteTab = tabs.find((tab) => tab.id !== tabId && !tab.completed);

    if (!nextIncompleteTab) {
      return setIsCompleted(true);
    }

    setSelectedTabId(nextIncompleteTab.id);
  };

  const getIsLastIncompleteTab = (tabId: TabId) => tabs.filter((tab) => tab.id !== tabId).every((tab) => tab.completed);

  return (
    <>
      <Dialog.Root onOpenChange={(open) => !open && onClose()} open={!isCompleted}>
        <Dialog.Body>
          <Dialog.Header
            description={t('settings:accountCompletionModal.description')}
            title={t('settings:accountCompletionModal.title')}
          />

          <Tabs.Root onValueChange={(value) => isTabId(value) && setSelectedTabId(value)} value={selectedTabId}>
            <Tabs.List className={tabListStyle}>
              {tabs.map((tab) => (
                <Tabs.Tab icon={tab.completed ? 'Check' : undefined} key={tab.id} value={tab.id}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {tabs.map((tab) => {
              const tabProps = { onCompleted: onTabCompleted(tab.id), isLastTab: getIsLastIncompleteTab(tab.id) };
              return (
                <Tabs.Panel className={tabContentStyle} key={tab.id} value={tab.id}>
                  {tab.id === 'financialInfo' && <FinancialInfoTab {...tabProps} />}

                  {tab.id === 'vatRegulation' && <VatRegulationTab {...tabProps} />}

                  {tab.id === 'frameworkAgreement' && <FrameworkAgreementTab {...tabProps} />}

                  {tab.id === 'peppol' && <PeppolTab {...tabProps} />}
                </Tabs.Panel>
              );
            })}
          </Tabs.Root>
        </Dialog.Body>
      </Dialog.Root>

      <AlertDialog.Root onOpenChange={(open) => !open && onComplete()} open={isCompleted}>
        <AlertDialog.Body status="success">
          <Illustration className={successIllustrationStyle} name="HandsHeart" strokeWidth={0.9} />

          <AlertDialog.Title>{t('settings:accountCompletionModal.successModal.title')}</AlertDialog.Title>
          <AlertDialog.Description>{t('settings:accountCompletionModal.successModal.description')}</AlertDialog.Description>

          <AlertDialog.Actions>
            <Button onClick={onComplete}>{t('common:actions.close')}</Button>
          </AlertDialog.Actions>
        </AlertDialog.Body>
      </AlertDialog.Root>
    </>
  );
};
